import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, Download, Upload, CheckCircle, Clock, AlertTriangle,
  Calendar, DollarSign, Save, Eye, Printer, RefreshCw, Info
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../services/api';

const WorkerTaxForms = ({ worker }) => {
  const [activeSection, setActiveSection] = useState('w4');
  const [w4Data, setW4Data] = useState({
    filing_status: 'single',
    multiple_jobs: false,
    dependents_amount: 0,
    other_income: 0,
    deductions: 0,
    extra_withholding: 0,
    exempt: false,
    signed_date: '',
    signature: ''
  });
  const [w2List, setW2List] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasW4, setHasW4] = useState(false);

  useEffect(() => {
    fetchTaxData();
  }, []);

  const fetchTaxData = async () => {
    setLoading(true);
    try {
      const [w4Res, w2Res, deductionsRes] = await Promise.all([
        api.get('/workers/me/tax-forms/w4').catch(() => ({ data: null })),
        api.get('/workers/me/tax-forms/w2').catch(() => ({ data: [] })),
        api.get('/workers/me/deductions').catch(() => ({ data: [] }))
      ]);
      
      if (w4Res.data) {
        setW4Data(w4Res.data);
        setHasW4(true);
      }
      setW2List(w2Res.data || []);
      setDeductions(deductionsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch tax data', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveW4 = async () => {
    if (!w4Data.signature) {
      toast.error('Please sign the form before submitting');
      return;
    }
    setSaving(true);
    try {
      await api.post('/workers/me/tax-forms/w4', {
        ...w4Data,
        signed_date: new Date().toISOString()
      });
      toast.success('W-4 form submitted successfully!');
      setHasW4(true);
      fetchTaxData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit W-4');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadW2 = async (year) => {
    try {
      const response = await api.get(`/workers/me/tax-forms/w2/${year}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `W2_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('W-2 not available for download yet');
    }
  };

  const filingStatusOptions = [
    { value: 'single', label: 'Single or Married filing separately' },
    { value: 'married_jointly', label: 'Married filing jointly' },
    { value: 'head_of_household', label: 'Head of household' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'w4', label: 'W-4 Form', icon: FileText },
          { id: 'w2', label: 'W-2 Forms', icon: Calendar },
          { id: 'deductions', label: 'Deductions', icon: DollarSign }
        ].map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
              activeSection === section.id
                ? 'bg-[var(--primary)] text-[var(--primary-text)]'
                : 'bg-[var(--background-subtle)] hover:bg-[var(--accent)]'
            }`}
          >
            <section.icon className="w-4 h-4" />
            {section.label}
          </button>
        ))}
      </div>

      {/* W-4 Section */}
      <AnimatePresence mode="wait">
        {activeSection === 'w4' && (
          <motion.div
            key="w4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="card p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[var(--primary)]" />
                  Form W-4 - Employee's Withholding Certificate
                </h3>
                <p className="text-sm text-[var(--text-muted)] mt-1">
                  Complete this form so your employer can withhold the correct federal income tax from your pay.
                </p>
              </div>
              {hasW4 && (
                <div className="flex items-center gap-2 text-[var(--success)]">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">Submitted</span>
                </div>
              )}
            </div>

            <div className="space-y-6">
              {/* Step 1: Personal Information */}
              <div className="p-4 bg-[var(--background-subtle)] rounded-lg">
                <h4 className="font-semibold mb-3">Step 1: Enter Personal Information</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input value={worker?.full_name || ''} disabled className="input-styled bg-[var(--background-paper)]" />
                  </div>
                  <div>
                    <Label>Social Security Number</Label>
                    <Input value="XXX-XX-XXXX" disabled className="input-styled bg-[var(--background-paper)]" />
                    <p className="text-xs text-[var(--text-muted)] mt-1">On file with HR</p>
                  </div>
                </div>
                <div className="mt-4">
                  <Label>Filing Status</Label>
                  <select
                    value={w4Data.filing_status}
                    onChange={(e) => setW4Data(prev => ({ ...prev, filing_status: e.target.value }))}
                    className="w-full mt-1 p-2 border border-[var(--border-primary)] rounded-lg bg-[var(--background-subtle)] text-[var(--text-primary)]"
                  >
                    {filingStatusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Step 2: Multiple Jobs */}
              <div className="p-4 bg-[var(--background-subtle)] rounded-lg">
                <h4 className="font-semibold mb-3">Step 2: Multiple Jobs or Spouse Works</h4>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={w4Data.multiple_jobs}
                    onChange={(e) => setW4Data(prev => ({ ...prev, multiple_jobs: e.target.checked }))}
                    className="w-4 h-4 rounded"
                  />
                  <p className="text-sm text-[var(--text-muted)]">
                  Complete this step if you (1) hold more than one job at a time, or (2) are married filing jointly and your spouse also works.
                </p>
                </label>
              </div>

              {/* Step 3: Dependents */}
              <div className="p-4 bg-[var(--background-subtle)] rounded-lg">
                <h4 className="font-semibold mb-3">Step 3: Claim Dependents</h4>
                <div>
                  <Label>Total amount for dependents ($)</Label>
                  <Input
                    type="number"
                    value={w4Data.dependents_amount}
                    onChange={(e) => setW4Data(prev => ({ ...prev, dependents_amount: parseFloat(e.target.value) || 0 }))}
                    className="input-styled"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-[var(--text-muted)] mt-1">
                    $2,000 per qualifying child under 17, $500 for other dependents
                  </p>
                </div>
              </div>

              {/* Step 4: Other Adjustments */}
              <div className="p-4 bg-[var(--background-subtle)] rounded-lg">
                <h4 className="font-semibold mb-3">Step 4: Other Adjustments (Optional)</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>(a) Other income ($)</Label>
                    <Input
                      type="number"
                      value={w4Data.other_income}
                      onChange={(e) => setW4Data(prev => ({ ...prev, other_income: parseFloat(e.target.value) || 0 }))}
                      className="input-styled"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>(b) Deductions ($)</Label>
                    <Input
                      type="number"
                      value={w4Data.deductions}
                      onChange={(e) => setW4Data(prev => ({ ...prev, deductions: parseFloat(e.target.value) || 0 }))}
                      className="input-styled"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>(c) Extra withholding ($)</Label>
                    <Input
                      type="number"
                      value={w4Data.extra_withholding}
                      onChange={(e) => setW4Data(prev => ({ ...prev, extra_withholding: parseFloat(e.target.value) || 0 }))}
                      className="input-styled"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              {/* Step 5: Sign */}
              <div className="p-4 bg-[var(--primary)]/10 rounded-lg border border-[var(--primary)]/30">
                <h4 className="font-semibold mb-3">Step 5: Sign Here</h4>
                <div className="mb-4">
                  <Label>Electronic Signature (Type your full name)</Label>
                  <Input
                    value={w4Data.signature}
                    onChange={(e) => setW4Data(prev => ({ ...prev, signature: e.target.value }))}
                    className="input-styled"
                    placeholder="Type your full legal name"
                  />
                </div>
                <p className="text-sm text-[var(--text-muted)]">
                  Under penalties of perjury, I declare that this certificate, to the best of my knowledge and belief, is true, correct, and complete.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  onClick={handleSaveW4}
                  disabled={saving || !w4Data.signature}
                  className="btn-primary"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Submit W-4
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* W-2 Section */}
        {activeSection === 'w2' && (
          <motion.div
            key="w2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="card p-6"
          >
            <h3 className="text-xl font-bold flex items-center gap-2 mb-6">
              <Calendar className="w-5 h-5 text-[var(--primary)]" />
              W-2 Wage and Tax Statements
            </h3>

            {w2List.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
                <h4 className="text-lg font-semibold mb-2">No W-2 Forms Available</h4>
                <p className="text-[var(--text-muted)]">
                  W-2 forms are generated at the end of each tax year. Check back in January for the previous year&apos;s form.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {w2List.map((w2) => (
                  <div
                    key={w2.year}
                    className="flex items-center justify-between p-4 bg-[var(--background-subtle)] rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-[var(--primary)]/20 flex items-center justify-center">
                        <FileText className="w-6 h-6 text-[var(--primary)]" />
                      </div>
                      <div>
                        <p className="font-semibold">W-2 Tax Year {w2.year}</p>
                        <p className="text-sm text-[var(--text-muted)]">
                          Total Wages: ${w2.total_wages?.toLocaleString() || '0.00'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadW2(w2.year)}
                        className="gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 p-4 bg-[var(--info)]/10 rounded-lg border border-[var(--info)]/30">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-[var(--info)] flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium">About W-2 Forms</p>
                  <p className="text-[var(--text-muted)] mt-1">
                    Employers must provide W-2 forms by January 31st for the previous tax year. 
                    Use this form when filing your federal and state income taxes.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Deductions Section */}
        {activeSection === 'deductions' && (
          <motion.div
            key="deductions"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="card p-6"
          >
            <h3 className="text-xl font-bold flex items-center gap-2 mb-6">
              <DollarSign className="w-5 h-5 text-[var(--primary)]" />
              Payroll Deductions
            </h3>

            {deductions.length === 0 ? (
              <div className="text-center py-12">
                <DollarSign className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
                <h4 className="text-lg font-semibold mb-2">No Active Deductions</h4>
                <p className="text-[var(--text-muted)]">
                  You don&apos;t have any payroll deductions set up. Contact your manager to add deductions.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[var(--border-primary)]">
                        <th className="text-left py-3 px-4">Deduction Type</th>
                        <th className="text-left py-3 px-4">Amount</th>
                        <th className="text-left py-3 px-4">Frequency</th>
                        <th className="text-left py-3 px-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deductions.map((deduction) => (
                        <tr key={deduction.id} className="border-b border-[var(--border-subtle)]">
                          <td className="py-3 px-4">
                            <div className="font-medium">{deduction.type}</div>
                            {deduction.description && (
                              <div className="text-sm text-[var(--text-muted)]">{deduction.description}</div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {deduction.is_percentage 
                              ? `${deduction.amount}%` 
                              : `$${deduction.amount.toFixed(2)}`
                            }
                          </td>
                          <td className="py-3 px-4 capitalize">{deduction.frequency}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              deduction.is_active 
                                ? 'bg-[var(--success)]/20 text-[var(--success)]' 
                                : 'bg-[var(--text-muted)]/20 text-[var(--text-muted)]'
                            }`}>
                              {deduction.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Summary */}
                <div className="mt-4 p-4 bg-[var(--background-subtle)] rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Per-Pay-Period Deductions:</span>
                    <span className="text-lg font-bold text-[var(--primary)]">
                      ${deductions.filter(d => d.is_active && !d.is_percentage).reduce((sum, d) => sum + d.amount, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WorkerTaxForms;
